import { useEffect } from 'react';
import type { Page } from '../../schemas/validation';

interface UseLinkInterceptionProps {
  isEmbedded: boolean;
  siteId: string | undefined;
  sitePages: Page[];
}

/** The page slug a preview link is asking for. Empty string is the home page. */
export function previewLinkSlug(href: string): string | null {
  const hashIdx = href.indexOf('#');
  const pathOnly = (hashIdx >= 0 ? href.slice(0, hashIdx) : href).split('?')[0];

  const websitePage = pathOnly.match(/\/sites\/[^/]+\/preview\/page\/([^/]+)\/?$/);
  if (websitePage) return decodeURIComponent(websitePage[1]);
  if (/\/sites\/[^/]+\/preview\/?$/.test(pathOnly)) return '';

  // Leave other app screens alone.
  if (pathOnly.startsWith('/sites/')) return null;

  return pathOnly.startsWith('/') ? pathOnly.slice(1) : pathOnly;
}

export const useLinkInterception = ({
  isEmbedded,
  siteId,
  sitePages,
}: UseLinkInterceptionProps) => {
  useEffect(() => {
    if (!siteId) return;

    console.log('🔗 Preview: Link interception enabled', {
      siteId,
      pagesCount: sitePages.length,
    });

    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a');
      if (!anchor) return;

      const href = anchor.getAttribute('href');
      console.log('🔗 Preview: Link clicked', { href });
      if (!href) return;

      if (
        href.startsWith('http://') ||
        href.startsWith('https://') ||
        href.startsWith('mailto:') ||
        href.startsWith('tel:')
      ) {
        console.log('🔗 Preview: External link, skipping');
        return;
      }

      if (href.startsWith('#')) {
        console.log('🔗 Preview: Anchor link, skipping');
        return;
      }

      // Allow app-level routes to pass through (not site pages)
      const appRoutes = ['/pricing', '/login', '/register', '/dashboard', '/admin'];
      if (appRoutes.some(route => href === route || href.startsWith(route + '/'))) {
        console.log('🔗 Preview: App route, allowing navigation:', href);
        return;
      }

      const hashIdx = href.indexOf('#');
      const hashFragment = hashIdx >= 0 ? href.slice(hashIdx) : '';
      const slug = previewLinkSlug(href);
      if (slug === null) return;

      console.log(
        '🔗 Preview: Looking for page with slug:',
        slug,
        'in',
        sitePages.map(p => p.slug)
      );

      const targetPage = slug
        ? sitePages.find(p => p.slug === slug || (slug === 'home' && p.isHomePage))
        : (sitePages.find(p => p.isHomePage) ?? sitePages[0]);

      if (targetPage) {
        console.log('🔗 Preview: Found page, navigating to preview:', targetPage.id);
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        const embed = isEmbedded ? '?embed=true' : '';
        window.location.href = `/preview/${siteId}/${targetPage.id}${embed}${hashFragment}`;
      } else {
        console.log('🔗 Preview: Page not found for slug:', slug);
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
      }
    };

    document.addEventListener('click', handleClick, { capture: true });
    return () => document.removeEventListener('click', handleClick, { capture: true });
  }, [isEmbedded, siteId, sitePages]);
};
